import { useMemo, useState } from 'react';
import { clsx } from 'clsx';
import type { ToothDef } from '../api/referralsApi';
import styles from './ToothChart.module.css';

type Numbering = 'universal' | 'fdi';

interface ToothChartProps {
  teeth: ToothDef[];
  selected: string[];
  onChange?: (codes: string[]) => void;
  numbering?: Numbering;
  onNumberingChange?: (n: Numbering) => void;
  readOnly?: boolean;
  /** Chart drawing width; the SVG scales with its container, this sets the aspect box. */
  compact?: boolean;
}

const VIEW_W = 520;
const VIEW_H = 600;
const UPPER = { cx: 260, cy: 205, rx: 200, ry: 160, prx: 126, pry: 100 };
const LOWER = { cx: 260, cy: 395, rx: 200, ry: 160, prx: 126, pry: 100 };
/** Molars sit a little past the horizontal, so the arches read as a U, not a semicircle. */
const OVERSHOOT = 0.42;
const PRIMARY_OVERSHOOT = 0.18;

const QUADRANT_LABEL: Record<ToothDef['quadrant'], string> = {
  UR: 'Upper Right',
  UL: 'Upper Left',
  LL: 'Lower Left',
  LR: 'Lower Right',
};

interface Placed {
  tooth: ToothDef;
  x: number;
  y: number;
  lx: number;
  ly: number;
  rx: number;
  ry: number;
  rotate: number;
}

function sizeFor(t: ToothDef): { rx: number; ry: number } {
  const base =
    t.type === 'molar'
      ? { rx: 17, ry: 15 }
      : t.type === 'premolar'
        ? { rx: 13.5, ry: 13 }
        : t.type === 'canine'
          ? { rx: 11.5, ry: 14 }
          : { rx: 11, ry: 13.5 };
  return t.primary ? { rx: base.rx * 0.78, ry: base.ry * 0.78 } : base;
}

function place(teeth: ToothDef[]): Placed[] {
  const out: Placed[] = [];
  const arches: Array<{ arch: 'upper' | 'lower'; primary: boolean }> = [
    { arch: 'upper', primary: false },
    { arch: 'upper', primary: true },
    { arch: 'lower', primary: false },
    { arch: 'lower', primary: true },
  ];
  for (const { arch, primary } of arches) {
    const list = teeth.filter((t) => t.arch === arch && t.primary === primary);
    // Order left-to-right as the viewer sees it: patient's right (UR/LR) on the viewer's left.
    const ordered = [...list].sort((a, b) => {
      const sideA = a.quadrant.endsWith('R') ? 0 : 1;
      const sideB = b.quadrant.endsWith('R') ? 0 : 1;
      if (sideA !== sideB) return sideA - sideB;
      return sideA === 0 ? b.position - a.position : a.position - b.position;
    });
    const geo = arch === 'upper' ? UPPER : LOWER;
    const rxA = primary ? geo.prx : geo.rx;
    const ryA = primary ? geo.pry : geo.ry;
    const over = primary ? PRIMARY_OVERSHOOT : OVERSHOOT;
    const n = ordered.length;
    const span = Math.PI + 2 * over;
    ordered.forEach((tooth, i) => {
      // θ runs from the viewer's left (π + over) to the right (−over).
      const theta = Math.PI + over - ((i + 0.5) * span) / n;
      const sin = Math.sin(theta);
      const cos = Math.cos(theta);
      const dir = arch === 'upper' ? -1 : 1;
      const x = geo.cx + rxA * cos;
      const y = geo.cy + dir * ryA * sin;
      const labelOffset = primary ? -24 : 28;
      const lx = geo.cx + (rxA + labelOffset) * cos;
      const ly = geo.cy + dir * (ryA + labelOffset) * sin;
      const { rx, ry } = sizeFor(tooth);
      out.push({ tooth, x, y, lx, ly, rx, ry, rotate: 90 - (theta * 180) / Math.PI });
    });
  }
  return out;
}

export function ToothChart({
  teeth,
  selected,
  onChange,
  numbering = 'universal',
  onNumberingChange,
  readOnly = false,
  compact = false,
}: ToothChartProps) {
  const placed = useMemo(() => place(teeth), [teeth]);
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const [hover, setHover] = useState<ToothDef | null>(null);
  const permanentCodes = useMemo(() => teeth.filter((t) => !t.primary).map((t) => t.code), [teeth]);
  const fullMouth = permanentCodes.length > 0 && permanentCodes.every((c) => selectedSet.has(c));

  const toggle = (code: string) => {
    if (readOnly || !onChange) return;
    onChange(selectedSet.has(code) ? selected.filter((c) => c !== code) : [...selected, code]);
  };

  const toggleFullMouth = () => {
    if (readOnly || !onChange) return;
    onChange(fullMouth ? selected.filter((c) => !permanentCodes.includes(c)) : [...new Set([...selected, ...permanentCodes])]);
  };

  const label = (t: ToothDef) => (numbering === 'fdi' ? t.fdi : t.code);

  return (
    <div className={clsx(styles.wrap, compact && styles.compact)}>
      {!readOnly && (
        <div className={styles.toolbar}>
          <button type="button" className={clsx(styles.fullMouth, fullMouth && styles.fullMouthOn)} onClick={toggleFullMouth}>
            {fullMouth ? 'Clear full mouth' : 'Select full mouth'}
          </button>
          {onNumberingChange && (
            <div className={styles.numbering} role="group" aria-label="Tooth numbering">
              <button
                type="button"
                className={clsx(styles.numberingBtn, numbering === 'universal' && styles.numberingOn)}
                onClick={() => onNumberingChange('universal')}
              >
                Universal
              </button>
              <button
                type="button"
                className={clsx(styles.numberingBtn, numbering === 'fdi' && styles.numberingOn)}
                onClick={() => onNumberingChange('fdi')}
              >
                FDI
              </button>
            </div>
          )}
        </div>
      )}
      <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} className={styles.svg} role="img" aria-label="Tooth chart">
        <text x={16} y={22} className={styles.quadrant}>
          Upper Right
        </text>
        <text x={VIEW_W - 16} y={22} className={styles.quadrant} textAnchor="end">
          Upper Left
        </text>
        <text x={16} y={VIEW_H - 12} className={styles.quadrant}>
          Lower Right
        </text>
        <text x={VIEW_W - 16} y={VIEW_H - 12} className={styles.quadrant} textAnchor="end">
          Lower Left
        </text>
        {placed.map(({ tooth, x, y, lx, ly, rx, ry, rotate }) => {
          const on = selectedSet.has(tooth.code);
          return (
            <g
              key={tooth.code}
              className={clsx(styles.tooth, on && styles.toothOn, readOnly && styles.toothReadOnly, tooth.primary && styles.primary)}
              onClick={() => toggle(tooth.code)}
              onMouseEnter={() => setHover(tooth)}
              onMouseLeave={() => setHover(null)}
              role={readOnly ? undefined : 'checkbox'}
              aria-checked={readOnly ? undefined : on}
              aria-label={`Tooth ${label(tooth)}, ${tooth.name}`}
              tabIndex={readOnly ? -1 : 0}
              onKeyDown={(e) => {
                if (e.key === ' ' || e.key === 'Enter') {
                  e.preventDefault();
                  toggle(tooth.code);
                }
              }}
            >
              <ellipse cx={x} cy={y} rx={rx} ry={ry} transform={`rotate(${rotate} ${x} ${y})`} className={styles.shape} />
              <text x={lx} y={ly} className={clsx(styles.label, on && styles.labelOn)} textAnchor="middle" dominantBaseline="middle">
                {label(tooth)}
              </text>
            </g>
          );
        })}
        <text x={VIEW_W / 2} y={(UPPER.cy + LOWER.cy) / 2 + 4} className={styles.hoverLabel} textAnchor="middle">
          {hover ? `${hover.quadrant} ${QUADRANT_LABEL[hover.quadrant]} · #${label(hover)}` : ''}
        </text>
      </svg>
      {selected.length > 0 && (
        <div className={styles.selectedRow} aria-live="polite">
          {fullMouth ? (
            <span className={styles.chip}>Full mouth</span>
          ) : (
            [...selected]
              .map((c) => teeth.find((t) => t.code === c))
              .filter((t): t is ToothDef => Boolean(t))
              .sort((a, b) => teeth.indexOf(a) - teeth.indexOf(b))
              .map((t) => (
                <button
                  key={t.code}
                  type="button"
                  className={styles.chip}
                  onClick={() => toggle(t.code)}
                  disabled={readOnly}
                  title={t.name}
                >
                  {label(t)}
                </button>
              ))
          )}
        </div>
      )}
    </div>
  );
}
